import { memo, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import { Call } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2';
import dayjs from 'dayjs';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useCallInsightController } from '@frontend/call-insight-panel';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useCallInsightHistory, CallInsightHistoryFilterType } from '@frontend/call-intel-insights';
import { theme } from '@frontend/theme';
import { useContactHistory } from '../providers';
import GroupedInfiniteList from './grouped-infinite-list';
import { HistoryEntry } from './history-entry';
import { HistoryFilters } from './history-filters';
import { HistoryGroupHeader } from './history-group-header';
import { HistoryPaginator } from './history-paginator';

/**
 * The `CallInsightHistoryPanel` component displays a paginated list of call insights grouped by month.
 * It allows users to filter, view, and load more call data.
 */
export const CallInsightHistoryPanel = memo(({ personId }: { personId: string }) => {
  const { filters } = useContactHistory();

  const insightFilters = filters.filter((filter) => filter.scope === 'patientInsight');

  const callInsightFilters = insightFilters.map((filter) => ({
    filterType: filter.filterType as CallInsightHistoryFilterType,
    key: filter.key,
    label: filter.label,
  }));

  const {
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    visiblePeriod,
    canLoadMore,
    isCurrentPeriodComplete,
    hasLoadedPreviousPeriods,
    visibleCalls,
    ...rest
  } = useCallInsightHistory({ personId, filters: callInsightFilters });

  const {
    open: openCallInsightPanel,
    setCallList,
    activeCall,
  } = useCallInsightController({ source: 'callHistoryContactPanel' });

  const selectedCall = activeCall?.callIntelInsights;

  const callListItems = useMemo(() => {
    if (!visibleCalls.length) return [];
    return visibleCalls.map(
      (call) =>
        ({
          channelId: call?.id || '',
          locationId: call?.locationId || '',
          callIntelData: call,
          includeCallIntelligence: false,
        } as const)
    );
  }, [visibleCalls]);

  useEffect(() => {
    setCallList(callListItems);
  }, [callListItems, setCallList]);

  return (
    <div css={styles.wrapper}>
      <div css={styles.stickyFilter}>
        <HistoryFilters />
      </div>
      <GroupedInfiniteList
        infiniteQueryProps={{ ...rest, fetchNextPage, hasNextPage, isFetchingNextPage }}
        groupBy={(call: Call) => dayjs(call.startTime).format('MMMM YYYY')} // Group calls by month and year
        renderGroupHeader={({ group, toggleGroup, ...rest }) => (
          <HistoryGroupHeader group={group} onToggle={() => toggleGroup(group)} {...rest} />
        )}
        renderItem={({ listItem }) => (
          <HistoryEntry
            kind='callIntel'
            data={listItem as CallIntelTypes.Call}
            selectedEntryId={selectedCall?.id}
            onClick={() =>
              openCallInsightPanel({
                channelId: listItem?.id ?? '',
                locationId: listItem?.locationId ?? '',
                callIntelData: listItem,
                includeCallIntelligence: false,
              })
            }
          />
        )}
        endOfListText={
          <HistoryPaginator
            hasNextPage={!!hasNextPage}
            isLoading={isFetchingNextPage}
            onLoadMore={fetchNextPage}
            visiblePeriod={visiblePeriod}
            canLoadMore={canLoadMore}
            isCurrentPeriodComplete={isCurrentPeriodComplete}
            hasLoadedPreviousPeriods={hasLoadedPreviousPeriods}
            trackingId={generateTrackingId({
              component: 'contact-panel-history',
              context: 'call-insight-load-more',
            })}
          />
        }
      />
    </div>
  );
});

const styles = {
  wrapper: css`
    background-color: ${theme.colors.neutral5};
    height: 100%; /* Ensure the list takes up the full height */
    overflow-y: auto; /* Enable scrolling */
  `,
  stickyFilter: css`
    position: sticky;
    top: 0;
    z-index: 10;
  `,
};

CallInsightHistoryPanel.displayName = 'CallInsightHistoryPanel';
